import React, { useMemo } from "react";
import { fmt } from "./utils";
import "./graphs.css";

/**
 * One (field, nutrient) trend series from the backend GraphModel_SoilTrend
 * payload. Mirrors the Swift GraphModelSoilTrend / Python graph_json shape.
 */
export interface SoilTrendSeries {
    field_name: string;
    customer_name: string;
    farm_name: string;
    nutrient_name: string;
    unit: string;
    color: string; // #RRGGBB from the DB ColorRamp
    target: number | null;
    points: { season: string; value: number }[];
}

interface SoilTrendChartProps {
    data: SoilTrendSeries[];
    /** When true, always render the band chart regardless of field count
     *  (the Summary / Q6 question). */
    forceBand?: boolean;
}

// Style is set by the question: Q5 per-field bars, Q6 band (forceBand).
const TARGET_COLOR = "#2f3741";

/** Natural "2" < "11" season sort. */
function seasonCompare(a: string, b: string): number {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/** Blend a #RRGGBB hex toward white by `amount` (0 = unchanged, 1 = white). */
function lighten(hex: string, amount: number): string {
    const h = hex.replace("#", "");
    if (h.length !== 6) return hex;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const mix = (c: number) => Math.round(c + (255 - c) * amount);
    const to2 = (c: number) => mix(c).toString(16).padStart(2, "0");
    return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function fieldLabel(s: SoilTrendSeries): string {
    return [s.customer_name, s.farm_name, s.field_name].filter(Boolean).join(" > ");
}

interface NutrientGroup {
    nutrient: string;
    unit: string;
    color: string;
    target: number | null;
    fields: { label: string; points: { season: string; value: number }[] }[];
}

/** Round the axis max up to a "nice" value and produce ~5 ticks. */
function niceTicks(vmax: number, count = 5): { ticks: number[]; max: number } {
    if (vmax <= 0) return { ticks: [0, 1], max: 1 };
    const rawStep = vmax / (count - 1);
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    let step = magnitude;
    for (const mult of [1, 2, 2.5, 5, 10]) {
        step = mult * magnitude;
        if (rawStep <= step) break;
    }
    const top = Math.ceil(vmax / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= top + step * 1e-9; v += step) {
        ticks.push(Math.round(v * 1e6) / 1e6);
    }
    return { ticks, max: top };
}

function NutrientLineChart({
    group,
    bandMode,
    perSeasonStyle = false,
}: {
    group: NutrientGroup;
    bandMode: boolean;
    // Q5 style: one full-width bar per season, shaded by season, value on bar.
    perSeasonStyle?: boolean;
}) {
    const width = 340;
    const height = 220;
    const pad = { top: 16, right: 56, bottom: 36, left: 50 };
    const plotW = width - pad.left - pad.right;
    const plotH = height - pad.top - pad.bottom;

    // Union of all seasons across this nutrient's fields, chronological.
    const seasons = useMemo(() => {
        const set = new Set<string>();
        group.fields.forEach((f) => f.points.forEach((p) => set.add(p.season)));
        return [...set].sort(seasonCompare);
    }, [group]);

    const allVals = group.fields.flatMap((f) => f.points.map((p) => p.value));
    const dataMax = Math.max(...allVals, group.target ?? 0, 0);
    const { ticks, max: yMax } = niceTicks(dataMax * 1.08);

    const n = Math.max(seasons.length - 1, 1);
    const sx = (seasonIdx: number) => pad.left + (seasonIdx / n) * plotW;
    const sy = (v: number) => pad.top + plotH - (v / yMax) * plotH;
    const seasonIdx = new Map(seasons.map((s, i) => [s, i]));

    const title = group.unit ? `${group.nutrient} (${group.unit})` : group.nutrient;

    // Band mode: compute per-season min/max/mean across all fields.
    const band = useMemo(() => {
        if (!bandMode) return [];
        return seasons
            .map((s, i) => {
                const vals = group.fields
                    .map((f) => f.points.find((p) => p.season === s)?.value)
                    .filter((v): v is number => v != null);
                if (vals.length === 0) return null;
                const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
                return { i, min: Math.min(...vals), max: Math.max(...vals), mean };
            })
            .filter((p): p is { i: number; min: number; max: number; mean: number } => p != null);
    }, [bandMode, seasons, group]);

    const bandAreaPath = () => {
        if (band.length < 2) return "";
        const top = band.map((p) => `${sx(p.i)},${sy(p.max)}`);
        const bottom = [...band].reverse().map((p) => `${sx(p.i)},${sy(p.min)}`);
        return `M${top.join(" L")} L${bottom.join(" L")} Z`;
    };
    const meanLinePath = () =>
        band.length >= 2 ? "M" + band.map((p) => `${sx(p.i)},${sy(p.mean)}`).join(" L") : "";

    return (
        <div className="soilTrendChartContainer">
            <div className="soilTrendChartTitle">{title}</div>
            <svg width={width} height={height} className="soilTrendSvg">
                {/* Gridlines + y tick labels */}
                {ticks.map((tv, i) => (
                    <g key={i}>
                        {tv > 0 && (
                            <line
                                x1={pad.left}
                                y1={sy(tv)}
                                x2={pad.left + plotW}
                                y2={sy(tv)}
                                stroke="#ffffff"
                                strokeOpacity={0.12}
                                strokeWidth={0.5}
                            />
                        )}
                        <text
                            x={pad.left - 5}
                            y={sy(tv) + 3}
                            textAnchor="end"
                            fontSize={9}
                            fill="#999"
                        >
                            {fmt(tv)}
                        </text>
                    </g>
                ))}

                {/* Axes */}
                <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + plotH} stroke="#666" />
                <line
                    x1={pad.left}
                    y1={pad.top + plotH}
                    x2={pad.left + plotW}
                    y2={pad.top + plotH}
                    stroke="#666"
                />

                {bandMode ? (
                    <>
                        <path d={bandAreaPath()} fill={lighten(group.color, 0.65)} opacity={0.85} />
                        <path d={meanLinePath()} fill="none" stroke={group.color} strokeWidth={2} />
                        {band.map((p) => (
                            <circle
                                key={p.i}
                                cx={sx(p.i)}
                                cy={sy(p.mean)}
                                r={2.5}
                                fill={group.color}
                            />
                        ))}
                    </>
                ) : perSeasonStyle ? (
                    // Q5 per-field: one bar per season, spread full-width (slot
                    // = plotW / pointCount), season-shaded, value on the bar.
                    (() => {
                        const pts = (group.fields[0]?.points ?? [])
                            .slice()
                            .sort((a, b) => seasonCompare(a.season, b.season));
                        const nPts = Math.max(pts.length, 1);
                        const slot = plotW / nPts;
                        const barW = Math.min(slot * 0.55, 22);
                        const baseY = pad.top + plotH;
                        return pts.map((p, i) => {
                            const cx = pad.left + slot * (i + 0.5);
                            const top = sy(p.value);
                            const h = baseY - top;
                            if (h <= 0) return null;
                            const shade = lighten(
                                group.color,
                                0.45 * (1 - (nPts > 1 ? i / (nPts - 1) : 1))
                            );
                            const label = fmt(p.value);
                            // Horizontal label: white inside the bar when it
                            // fits (tall and wide enough), else grey above it.
                            // ~4px per char at fontSize 7.
                            const fitsInside = h >= 14 && label.length * 4 <= barW - 2;
                            return (
                                <g key={i}>
                                    <rect
                                        x={cx - barW / 2}
                                        y={top}
                                        width={barW}
                                        height={h}
                                        fill={shade}
                                    />
                                    {fitsInside ? (
                                        <text
                                            x={cx}
                                            y={top + 8}
                                            fontSize={7}
                                            fontWeight="bold"
                                            fill="#ffffff"
                                            textAnchor="middle"
                                        >
                                            {label}
                                        </text>
                                    ) : (
                                        <text
                                            x={cx}
                                            y={top - 3}
                                            fontSize={7}
                                            fill="#999"
                                            textAnchor="middle"
                                        >
                                            {label}
                                        </text>
                                    )}
                                </g>
                            );
                        });
                    })()
                ) : (
                    // Grouped bars: one bar per field, group centred in its slot.
                    (() => {
                        const nFields = group.fields.length;
                        const nSeasons = Math.max(seasons.length, 1);
                        const slot = plotW / nSeasons;
                        const barW = Math.min((slot * 0.8) / Math.max(nFields, 1), 22);
                        const groupW = barW * nFields;
                        const baseY = pad.top + plotH;
                        const slotCenter = (i: number) => pad.left + (i + 0.5) * slot;
                        return group.fields.map((f, fi) => {
                            const shade = lighten(
                                group.color,
                                0.55 * (1 - (nFields > 1 ? fi / (nFields - 1) : 1))
                            );
                            return (
                                <g key={fi}>
                                    {f.points.map((p, pi) => {
                                        if (!seasonIdx.has(p.season)) return null;
                                        const cx = slotCenter(seasonIdx.get(p.season)!);
                                        const top = sy(p.value);
                                        const h = baseY - top;
                                        if (h <= 0) return null;
                                        const left = cx - groupW / 2 + fi * barW;
                                        const w = Math.max(barW - 0.8, 0.5);
                                        return (
                                            <rect
                                                key={pi}
                                                x={left + 0.4}
                                                y={top}
                                                width={w}
                                                height={h}
                                                fill={shade}
                                            />
                                        );
                                    })}
                                </g>
                            );
                        });
                    })()
                )}

                {/* Target line + label */}
                {group.target != null && (
                    <>
                        <line
                            x1={pad.left}
                            y1={sy(group.target)}
                            x2={pad.left + plotW}
                            y2={sy(group.target)}
                            stroke={TARGET_COLOR}
                            strokeWidth={1}
                            strokeDasharray="4 3"
                        />
                        <text
                            x={pad.left + plotW + 3}
                            y={sy(group.target) + 3}
                            fontSize={9}
                            fontWeight="bold"
                            fill={TARGET_COLOR}
                        >
                            Target {fmt(group.target)}
                        </text>
                    </>
                )}

                {/* Season x-axis labels (every other when crowded). Per-field
                    and grouped-bar modes align to slot centres; band mode sits
                    on the line-chart points. In per-field mode the slot count
                    is this field's own point count so labels match the bars. */}
                {(perSeasonStyle
                    ? (group.fields[0]?.points ?? [])
                          .slice()
                          .sort((a, b) => seasonCompare(a.season, b.season))
                          .map((p) => p.season)
                    : seasons
                ).map((s, i, arr) =>
                    i % (arr.length <= 8 ? 1 : 2) === 0 ? (
                        <text
                            key={s}
                            x={
                                bandMode
                                    ? sx(i)
                                    : pad.left + (i + 0.5) * (plotW / Math.max(arr.length, 1))
                            }
                            y={pad.top + plotH + 14}
                            textAnchor="middle"
                            fontSize={9}
                            fill="#999"
                        >
                            {s}
                        </text>
                    ) : null
                )}

                {/* y-axis unit title */}
                {group.unit && (
                    <text
                        x={12}
                        y={pad.top + plotH / 2}
                        textAnchor="middle"
                        fontSize={10}
                        fill="#ccc"
                        transform={`rotate(-90, 12, ${pad.top + plotH / 2})`}
                    >
                        {group.unit}
                    </text>
                )}
                {/* x-axis title */}
                <text
                    x={pad.left + plotW / 2}
                    y={height - 2}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#ccc"
                >
                    Season
                </text>
            </svg>
        </div>
    );
}

export default function SoilTrendChart({ data, forceBand = false }: SoilTrendChartProps) {
    const { groups, fieldLabels, bandMode } = useMemo(() => {
        const byNutrient = new Map<string, NutrientGroup>();
        const order: string[] = [];
        const labels: string[] = [];
        const seenLabels = new Set<string>();

        for (const s of data) {
            if (!byNutrient.has(s.nutrient_name)) {
                byNutrient.set(s.nutrient_name, {
                    nutrient: s.nutrient_name,
                    unit: s.unit,
                    color: s.color,
                    target: s.target,
                    fields: [],
                });
                order.push(s.nutrient_name);
            }
            const label = fieldLabel(s);
            byNutrient.get(s.nutrient_name)!.fields.push({ label, points: s.points });
            if (!seenLabels.has(label)) {
                seenLabels.add(label);
                labels.push(label);
            }
        }

        const grps = order.map((n) => byNutrient.get(n)!);
        // Band is set by the question (Q6), not field count.
        return {
            groups: grps,
            fieldLabels: labels,
            bandMode: forceBand,
        };
    }, [data, forceBand]);

    // Q5: regroup nutrient-first groups into per-field sections.
    const fieldSections = useMemo(() => {
        if (bandMode) return [];
        return fieldLabels.map((label) => {
            const charts: NutrientGroup[] = groups
                .map((g) => {
                    const fieldForGroup = g.fields.find((f) => f.label === label);
                    if (!fieldForGroup) return null;
                    return { ...g, fields: [fieldForGroup] } as NutrientGroup;
                })
                .filter((g): g is NutrientGroup => g != null);
            return { label, charts };
        });
    }, [bandMode, fieldLabels, groups]);

    if (!data || data.length === 0) return null;

    return (
        <div className="soilTrendSection">
            <h3 className="soilTrendTitle">Soil Test Results Trends</h3>
            {bandMode ? (
                <p className="soilTrendDescription">
                    Each chart shows a nutrient&apos;s trend by season across all{" "}
                    {fieldLabels.length} fields. The shaded band spans the lowest-to-highest field
                    value each season and the solid line is the average. The dashed line marks the
                    nutrient&apos;s target value.
                </p>
            ) : (
                <p className="soilTrendDescription">
                    Each chart shows a nutrient&apos;s average soil test result by season. The
                    dashed line marks the nutrient&apos;s target value.
                </p>
            )}

            {bandMode ? (
                <div className="soilTrendGrid">
                    {groups.map((g) => (
                        <NutrientLineChart key={g.nutrient} group={g} bandMode />
                    ))}
                </div>
            ) : (
                fieldSections.map((section) => (
                    <div key={section.label} className="soilTrendFieldSection">
                        <div className="soilTrendFieldHeading">{section.label}</div>
                        <div className="soilTrendGrid">
                            {section.charts.map((g) => (
                                <NutrientLineChart
                                    key={g.nutrient}
                                    group={g}
                                    bandMode={false}
                                    perSeasonStyle
                                />
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}
